/**
 * useVoiceRecording — MediaRecorder wrapper with volume + silence detection.
 *
 * Threshold visitors at office buildings are one-handed, often not looking
 * at the screen, and in noisy environments. The hook auto-stops on:
 *   1. caller tapping stop (returns the Blob)
 *   2. `maxDurationMs` hard cap (default 30s)
 *   3. `silenceMs` of continuous low-volume audio (default 2.5s)
 *
 * Auto-stop fires `onAutoStop` so the caller can advance the flow without
 * the visitor having to tap. The hook itself doesn't network — it only
 * captures audio and reports state.
 *
 * Permission failures, getUserMedia failures, and MediaRecorder failures
 * all surface as `state === 'error'` with a stable `errorCode`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

// The hook tracks ONLY the microphone-capture lifecycle. Network state
// (transcription request in-flight) is the caller's concern — ChatSheet
// tracks its own `isTranscribing` flag so the placeholder can flip to
// "Transcribing…" without us conflating two different lifecycles here.
export type VoiceState = 'idle' | 'requesting' | 'recording' | 'error';

export type VoiceErrorCode = 'permission_denied' | 'not_supported' | 'no_audio' | 'recorder_failed';

export interface UseVoiceRecordingOptions {
  /** Hard cap on recording duration. Default 30s. */
  maxDurationMs?: number;
  /** Continuous below-threshold duration that triggers auto-stop. Default 2500ms. */
  silenceMs?: number;
  /** RMS volume threshold (0-1) below which counts as silence. Default 0.02. */
  silenceThreshold?: number;
  /**
   * Fired when the hook auto-stops (silence OR maxDuration). Receives the
   * stop reason AND the recorded blob, so the caller can transcribe and
   * auto-submit without the visitor having to tap stop. This is the
   * zero-friction "stopped talking → submitted" path.
   */
  onAutoStop?: (reason: 'silence' | 'cap', durationMs: number, blob: Blob | null) => void;
}

export interface UseVoiceRecording {
  state: VoiceState;
  volume: number;
  errorCode: VoiceErrorCode | null;
  /** Begin recording. Resolves when MediaRecorder has actually started. */
  start: () => Promise<void>;
  /** Stop and return the recorded Blob. Resolves null on error or empty recording. */
  stop: () => Promise<Blob | null>;
  /** Discard the recording and release the microphone without returning a Blob. */
  cancel: () => void;
}

const DEFAULTS = {
  maxDurationMs: 30_000,
  silenceMs: 2500,
  silenceThreshold: 0.02,
};

export function useVoiceRecording(options: UseVoiceRecordingOptions = {}): UseVoiceRecording {
  const opts = { ...DEFAULTS, ...options };

  const [state, setState] = useState<VoiceState>('idle');
  const [volume, setVolume] = useState(0);
  const [errorCode, setErrorCode] = useState<VoiceErrorCode | null>(null);

  const recorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const audioCtxRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const lastSoundAtRef = useRef<number>(0);
  const capTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafRef = useRef<number | null>(null);
  // Auto-stop reason captured before stop() resolves, so onAutoStop fires once
  // with the correct reason and the consumer can pass it into telemetry.
  const autoStopReasonRef = useRef<'silence' | 'cap' | null>(null);
  // Stop() returns a Blob via this resolver after the 'stop' event fires.
  const stopResolverRef = useRef<((blob: Blob | null) => void) | null>(null);

  // Pick a supported audio mime type. Chrome/Firefox prefer webm/opus; iOS
  // Safari only supports mp4/aac. Empty string lets the browser default.
  const pickMimeType = (): string => {
    const candidates = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];
    if (typeof MediaRecorder !== 'undefined' && typeof MediaRecorder.isTypeSupported === 'function') {
      for (const m of candidates) {
        if (MediaRecorder.isTypeSupported(m)) return m;
      }
    }
    return '';
  };

  const releaseResources = useCallback(() => {
    if (rafRef.current !== null) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (capTimerRef.current) {
      clearTimeout(capTimerRef.current);
      capTimerRef.current = null;
    }
    try {
      sourceRef.current?.disconnect();
    } catch {
      /* ignore */
    }
    sourceRef.current = null;
    analyserRef.current = null;
    if (audioCtxRef.current && audioCtxRef.current.state !== 'closed') {
      audioCtxRef.current.close().catch(() => {
        /* ignore */
      });
    }
    audioCtxRef.current = null;
    if (streamRef.current) {
      for (const track of streamRef.current.getTracks()) track.stop();
    }
    streamRef.current = null;
    recorderRef.current = null;
  }, []);

  // Cleanup on unmount — if a visitor closes the chat sheet mid-recording
  // the mic must release immediately or the OS indicator stays on.
  useEffect(() => {
    return () => {
      releaseResources();
      stopResolverRef.current = null;
    };
  }, [releaseResources]);

  const start = useCallback(async () => {
    if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setErrorCode('not_supported');
      setState('error');
      return;
    }

    // Initiate getUserMedia FIRST, before any setState or other work, so the
    // user-activation context from the click is preserved. Chrome on Android
    // is strict here — if the Promise isn't created in the same synchronous
    // tick as the tap, the permission prompt's Continue button silently
    // fails to grant. Setting state after the request is fine; the Promise
    // has already crossed the gesture boundary.
    const streamPromise = navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true },
    });

    setErrorCode(null);
    setState('requesting');

    let stream: MediaStream;
    try {
      stream = await streamPromise;
    } catch {
      // Both NotAllowedError and SecurityError land here; treat as denied.
      setErrorCode('permission_denied');
      setState('error');
      return;
    }
    streamRef.current = stream;

    let recorder: MediaRecorder;
    const mime = pickMimeType();
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch {
      releaseResources();
      setErrorCode('recorder_failed');
      setState('error');
      return;
    }
    recorderRef.current = recorder;
    chunksRef.current = [];
    startedAtRef.current = Date.now();
    lastSoundAtRef.current = Date.now();
    autoStopReasonRef.current = null;

    recorder.addEventListener('dataavailable', (ev) => {
      if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data);
    });

    recorder.addEventListener('stop', () => {
      const mimeType = recorder.mimeType || mime || 'audio/webm';
      const blob = chunksRef.current.length > 0 ? new Blob(chunksRef.current, { type: mimeType }) : null;
      const duration = Date.now() - startedAtRef.current;
      // Snapshot the auto-stop reason before we tear down so the caller's
      // onAutoStop handler sees the same state the hook just transitioned through.
      const reason = autoStopReasonRef.current;
      releaseResources();
      // Capture is done; release the state machine so the caller can re-enable
      // the input and re-render the mic button. Network/transcription is
      // tracked separately by the caller (see ChatSheet `isTranscribing`).
      setState('idle');
      setVolume(0);
      // Auto-stop callback fires with the blob so the caller can transcribe
      // + auto-submit without waiting for a tap. Fires AFTER releaseResources
      // so the OS mic indicator is already off by the time we begin network.
      if (reason && opts.onAutoStop) opts.onAutoStop(reason, duration, blob);
      const resolver = stopResolverRef.current;
      stopResolverRef.current = null;
      if (resolver) resolver(blob);
    });

    // Volume + silence detection via Web Audio API.
    try {
      const AudioCtxClass: typeof AudioContext =
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ?? window.AudioContext;
      const ctx = new AudioCtxClass();
      audioCtxRef.current = ctx;
      const source = ctx.createMediaStreamSource(stream);
      sourceRef.current = source;
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);
      analyserRef.current = analyser;
      const buffer = new Float32Array(analyser.fftSize);

      const tick = () => {
        if (!analyserRef.current || !recorderRef.current) return;
        analyserRef.current.getFloatTimeDomainData(buffer);
        let sum = 0;
        for (let i = 0; i < buffer.length; i++) {
          const v = buffer[i] ?? 0;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / buffer.length);
        setVolume(rms);
        const now = Date.now();
        if (rms >= opts.silenceThreshold) lastSoundAtRef.current = now;
        // Only auto-stop on silence AFTER we've heard at least 600ms of sound
        // — otherwise immediate-silence will stop us before the visitor speaks.
        const elapsed = now - startedAtRef.current;
        if (elapsed > 600 && now - lastSoundAtRef.current >= opts.silenceMs) {
          autoStopReasonRef.current = 'silence';
          try {
            recorderRef.current.state === 'recording' && recorderRef.current.stop();
          } catch {
            /* ignore */
          }
          return;
        }
        rafRef.current = requestAnimationFrame(tick);
      };
      rafRef.current = requestAnimationFrame(tick);
    } catch {
      // If Web Audio fails we still record — just lose volume + silence
      // detection. The maxDuration cap below still applies.
    }

    capTimerRef.current = setTimeout(() => {
      if (!recorderRef.current) return;
      autoStopReasonRef.current = 'cap';
      try {
        recorderRef.current.state === 'recording' && recorderRef.current.stop();
      } catch {
        /* ignore */
      }
    }, opts.maxDurationMs);

    try {
      recorder.start();
    } catch {
      releaseResources();
      setErrorCode('recorder_failed');
      setState('error');
      return;
    }
    setState('recording');
  }, [opts, releaseResources]);

  const stop = useCallback((): Promise<Blob | null> => {
    return new Promise((resolve) => {
      const rec = recorderRef.current;
      if (!rec || rec.state !== 'recording') {
        resolve(null);
        return;
      }
      stopResolverRef.current = resolve;
      try {
        rec.stop(); // 'stop' event handler resets state to 'idle' + resolves
      } catch {
        stopResolverRef.current = null;
        resolve(null);
      }
    });
  }, []);

  const cancel = useCallback(() => {
    const rec = recorderRef.current;
    chunksRef.current = [];
    stopResolverRef.current = null;
    autoStopReasonRef.current = null;
    if (rec && rec.state === 'recording') {
      try {
        rec.stop();
      } catch {
        /* ignore */
      }
    }
    releaseResources();
    setState('idle');
    setVolume(0);
  }, [releaseResources]);

  return { state, volume, errorCode, start, stop, cancel };
}
